// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.Container.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------

import React, { Component, PropTypes } from 'react';
import { observe } from 'mobx';
import AppGlobal from './Izenda.AppGlobal';
import AppStore from 'logic/Izenda.AppStore';
import Cookies from 'Izenda.Cookies';
import SecurityManager from 'share/authorization/Izenda.SecurityManager';
import SecurityManagerStore from 'logic/Izenda.SecurityManagerStore';
import DialogsHub from 'share/components/controls/Izenda.DialogsHub';
import LoadingIndicator from 'share/components/controls/Izenda.LoadingIndicator';
import ExportLoadingIndicator from 'share/components/controls/Izenda.ExportLoadingIndicator';
import enhanceContext from 'share/enhancers/Izenda.EnhanceContext';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import SystemAlert from './Izenda.SystemAlert';
import uiState from 'logic/Izenda.UiState';
import routeUtils from 'share/utils/Izenda.RouteUtils';
import BrowserUtils from 'share/utils/Izenda.BrowserUtils';
import { withRouter } from 'react-router';
import classnames from 'classnames';
import NoPermission from 'share/components/controls/Izenda.NoPermission';
import AccountStore from 'logic/Izenda.AccountStore';
import localizer from 'share/localization/Izenda.Localizer';
import { isNil, get } from 'lodash/fp';
import SystemSettingFetcher from 'fetchers/Izenda.SystemSettingFetcher';
import NotificationFetcher from 'fetchers/Izenda.NotificationFetcher';

let DevTools;

if (__DEV__) {
  DevTools = require('mobx-react-devtools').default;
}

class Container extends Component {
  static contextTypes = {
    dialogs: PropTypes.object
  };

  static propTypes = {
    isNeedLoginForm: PropTypes.bool
  };

  static defaultProps = {
    isNeedLoginForm: true
  };

  constructor() {
    super();
    this.state = {
      store: AppStore,
      showPermissionError: false,
      isCookieOptionLoaded: false
    };

    this.securityManagerStore = new SecurityManagerStore();
    this.securityManager = new SecurityManager();
    this.accountStore = new AccountStore();
    this.getSecurity();

    this.updateNotificationsDisposer = observe(uiState, 'updateNotifications', () => {
      if(uiState.updateNotifications){
        NotificationFetcher.getAllNotifications().then(result => {
          uiState.unReadNotificationsCount = result.data.unReadCount;
          uiState.notifications = result.data.notifications;
        });
        uiState.resetUpdateNotifications();
      }
    });

    this.needLoginDisposer = observe(uiState, 'needLogin', this.handleNeedLogin);
    this.needChangeGoogleApiKey = observe(uiState, 'tenantId', this.loadGoogleApiKey);
    uiState.setMobileVersion(BrowserUtils.isMobileDevice());

    // Check Dirty for Integration Mode
    window.onbeforeunload = () => {
      // A popup with text 'null' will be displayed if this return null in IE browser
      if (uiState.isDirty) {
        return localizer.getText('COMMON_MESSAGES_CONFIRM_CHANGES_LOST');
      }
    };

    // Notify image converter tool to convert the image when error
    window.onerror = () => {
      window.status = 'completed';
    };
  }

  handleNeedLogin = () => {
    if (uiState.needLogin) {
      const autoAuthToken = this.securityManager.getAutoAuthToken();
      if (!isNil(autoAuthToken)) {
        this.securityManager.authWithAutoAuthToken(autoAuthToken, () => {
          this.setState({ showPermissionError: false });
        }, () => {
          this.setState({ showPermissionError: true });
        });
      } else {
        if (this.props.isNeedLoginForm) {
          this.securityManager.clear();
          this.context.dialogs.reset();
          const loginLink = routeUtils.getRouteUrl(routeUtils.getConfigRoute('Account', 'Login'), {
            redirectTo: this.props.location.pathname + this.props.location.search
          });
          this.props.router.push(loginLink);
        } else {
          this.setState({ showPermissionError: true });
        }
      }
    }
  };

  loadGoogleApiKey = () => {
    SystemSettingFetcher.loadGoogleMapConfiguration(uiState.tenantId).then(result => {
      const googleAiKey = get('googleAPIKey', result);
      uiState.setGoogleApiKey(googleAiKey);
    });
  };

  componentDidMount() {
    this.state.store.getProvisionMapDataStatus();
    this.ensureDatabaseConfigured();
  }

  componentWillUnmount() {
    this.needLoginDisposer();
    this.updateNotificationsDisposer();
  }

  componentDidUpdate() {
    this.getSecurity();
  }

  /**
   * Ensure database configured
   */
  ensureDatabaseConfigured() {
    this.accountStore.checkDatabaseSetup().then(dbConfigured => {
      if (!dbConfigured) {
        this.securityManager.clear();
        const settingLink = routeUtils.getSettingsUrl(null, { notSetupDatabase: true });

        // Redirect to setting page
        if (this.props.router) {
          this.props.router.push(settingLink);
        } else {
          window.location.href = settingLink;
        }
      }

      return dbConfigured;
    });
  }

  /**
   * Get security data
   */
  getSecurity() {
    if(!this.state.isCookieOptionLoaded){
      SystemSettingFetcher.getCookieOptions().then(options => {
        Cookies.setDefaultOptions(options);
        this.state.isCookieOptionLoaded = true;
      });
    }

    const accessToken = this.securityManager.getAccessToken();
    if (!isNil(accessToken)) {
      this.securityManagerStore.fetchData();
    }
  }

  checkPrintMode() {
    const props = this.props;
    const query = props.location && props.location.query;
    return query && query.print === 'true';
  }

  setContainerElement(container) {
    AppGlobal.containerElement = container;
  }

  render() {
    const dialogs = this.context.dialogs;
    const showPermissionError = this.state.showPermissionError;
    const accessToken = this.securityManager.getAccessToken();
    const isPrintMode = this.checkPrintMode();
    const className = classnames('izenda page', {
      u_overflowVisible: isPrintMode,
      u_inline: isPrintMode,
     // u_overflowHidden: !isPrintMode,
      'izenda-print': isPrintMode
    });

    return (
      (isNil(accessToken) || this.securityManager.isCompleteLoad) && (
        <div className='izenda-root izenda-container'>
          <section ref={this.setContainerElement} className={className}>
            {showPermissionError ? <NoPermission /> : this.props.children}
            <DialogsHub dialog={dialogs.child} />
            <LoadingIndicator />
            <ExportLoadingIndicator loaders={uiState.exportingLoadings} />
            {/* {!isPrintMode && __DEV__ ? <DevTools /> : null} */}
            <SystemAlert />
          </section>
        </div>
      )
    );
  }
}

const connectContainer = compose(
  withRouter,
  enhanceContext,
  observer
);
export default connectContainer(Container);



// WEBPACK FOOTER //
// ./ui/Izenda.Container.js